import { HotelChainRep } from "@/lib/types/hotel";
import { sanitizeForEmailText } from "@/lib/utils/sanitization";

/** First-person voice for the generic chain-rep sentence. */
type Voice = "singular" | "plural";

interface ChainRepMessageOptions {
  /** `"singular"` → "I've cc'd…" (legacy/admin), `"plural"` → "We've cc'd…" (enterprise). Defaults to `"singular"`. */
  voice?: Voice;
}

/**
 * Builds the chain-specific explanatory paragraph CC'ing hotel chain reps on an
 * RFP. Returns the `<p>…</p>` snippet only (no insertion logic), or `""` when
 * there are no reps. Hilton accounts get a managed-account / HWS preamble; all
 * other chains get a generic "cc'd our {chain} rep(s) {names}" sentence whose
 * first-person voice follows `opts.voice`.
 *
 * Interpolated rep names and the chain name are passed through `sanitizeForEmailText`
 * because callers (notably the enterprise template) inject the result into HTML
 * that is not otherwise run through an HTML sanitizer.
 *
 * @param chainReps - The chain reps being CC'd; an empty array yields `""`.
 * @param chainName - The chain name for display; `null`/empty falls back to "chain".
 * @param opts - Options; `voice` selects the singular/plural first person.
 * @returns An HTML `<p>` snippet, or `""` when there are no reps.
 */
export function buildChainRepMessageHtml(
  chainReps: HotelChainRep[],
  chainName: string | null | undefined,
  opts?: ChainRepMessageOptions
): string {
  if (chainReps.length === 0) {
    return "";
  }

  const voice: Voice = opts?.voice ?? "singular";
  const subject = voice === "plural" ? "We've" : "I've";

  // Reps with a usable first name drive the displayed names; pluralization still
  // counts every CC'd rep (matches legacy: a valid rep with a blank name counts).
  const repsWithNames = chainReps.filter((rep) => rep.first_name?.trim());
  const repNames = repsWithNames.map((rep) => sanitizeForEmailText(rep.first_name ?? "")).join(" and ");
  const hasMultipleReps = chainReps.length > 1;

  // Special message for Hilton accounts (already plural-voiced for both callers).
  // NOTE: pre-existing logic moved verbatim — the hardcoded chain name and the
  // first-name-based pronoun are tech debt; a data-driven per-chain copy + rep
  // pronoun field would be the deeper fix (out of scope for this parity change).
  if (chainName === "Hilton") {
    const pronoun = repsWithNames[0]?.first_name === "Priscilla" ? "She" : "They";
    if (repNames) {
      return `<p>Please see new RFP details below for your hotel. As we are a managed Hilton account, we are CC'ing ${repNames}, our Hilton Worldwide Sales (HWS) contact${hasMultipleReps ? "s" : ""}, for additional support. ${pronoun} will be sending the lead through meeting broker/FDC and in the meantime we are forwarding this email so you can start reviewing availability.</p>`;
    }
    return `<p>Please see new RFP details below for your hotel. As we are a managed Hilton account, we are CC'ing our Hilton Worldwide Sales (HWS) contact${hasMultipleReps ? "s" : ""} for additional support. They will be sending the lead through meeting broker/FDC and in the meantime we are forwarding this email so you can start reviewing availability.</p>`;
  }

  const displayChainName = sanitizeForEmailText(chainName || "chain");
  if (!repNames) {
    return `<p>${subject} cc'd our ${displayChainName} rep to help with expediting a response.</p>`;
  }
  if (repsWithNames.length === 1) {
    return `<p>${subject} cc'd our ${displayChainName} rep ${repNames} to help with expediting a response.</p>`;
  }
  return `<p>${subject} cc'd our ${displayChainName} reps ${repNames} to help with expediting a response.</p>`;
}
